import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AVAILABLE_LOCALES, RTL_LOCALES } from '@vcip/help';
import { AR_DECK } from './decks/ar';
import { EN_DECK } from './decks/en';
import { HI_DECK } from './decks/hi';

/**
 * The droom i18n runtime — DEC-053's Tier 2.5, Hindi as the proving run.
 *
 * The droom had NO internationalisation at all: zero i18n dependency,
 * `lang="en"` pinned in index.html, and ~15 screens of copy written straight
 * into JSX — while the officer console shipped 7 locales. Both audits called
 * that the largest strategic inversion in the product: it localised for the
 * trained professional at a desk and not for the anxious first-time customer
 * doing one-shot mobile video-KYC in India.
 *
 * DELIBERATELY NOT i18next. The console's help corpus needs ICU plurals across
 * 10 locales; the droom's copy rule (documentSteps.ts, ratified by INTAKE-08)
 * is FULL-SENTENCE keys with at most ONE `{slot}` — so the whole runtime is a
 * flat deck lookup plus one replace, and the customer's low-bandwidth mobile
 * bundle does not carry an ICU engine to render sentences that never use it.
 *
 * THE LOCALE SET IS DERIVED, NEVER RE-DECLARED (DEC-053's binding rider):
 * offerable = (the console's content-backed AVAILABLE_LOCALES) ∩ (droom decks
 * that are COMPLETE). A locale enters the droom automatically when it becomes
 * real in BOTH places, and never before. gu/mr/it are 0-key stubs in the
 * console and cannot appear here; fr/de/es/pt/ar are real in the console but
 * have no droom deck yet, so they do not appear either — until their deck ships
 * complete, at which point NOTHING here needs editing. A second hand-maintained
 * list is the failure DEC-053's rider exists to prevent.
 *
 * VC-12 adds ONE condition for PACK-GATED languages (Arabic): the session's pack must name it (`packLocales` on the
 * session read, be V223). An India session (no packLocales) offers exactly what it offered before Arabic existed. The
 * provider also stamps `dir` beside `lang` on <html>, so an Arabic journey mirrors from the root.
 */

export type DroomDeck = Readonly<Record<string, string>>;

/** locale → deck. Adding a locale = adding a COMPLETE deck file, nothing else. */
export const DECKS: Readonly<Record<string, DroomDeck>> = {
  en: EN_DECK,
  hi: HI_DECK,
  ar: AR_DECK,
};

/** VC-12 — languages offered only when the session's pack names them. */
export const PACK_GATED_LOCALES: readonly string[] = ['ar'];

/** The session pack's customer languages as the session read carries them; null/undefined when it declares none. */
export type PackLocales = readonly string[] | null | undefined;

function packAllows(locale: string, packLocales: PackLocales): boolean {
  return !PACK_GATED_LOCALES.includes(locale) || (Array.isArray(packLocales) && packLocales.includes(locale));
}

/** Right-to-left scripts (the help system's list — one source of truth). */
export function dirOf(locale: string): 'rtl' | 'ltr' {
  return (RTL_LOCALES as readonly string[]).includes(locale) ? 'rtl' : 'ltr';
}

/**
 * The tag dates and numbers are formatted with. Arabic keeps Latin digits (`-u-nu-latn`): references, OTPs, the PAN
 * format and every code on the wire are Latin digits, and a screen mixing ٣ and 3 makes the customer read one value two
 * ways. Every other locale is passed through unchanged (the India screens format exactly as before).
 */
export function intlLocaleOf(locale: string): string {
  return locale === 'ar' ? 'ar-u-nu-latn' : locale;
}

/** A deck counts only when it covers EVERY EN key — a customer must never see
 *  a mixed-language screen because a translator stopped halfway (the DEC-035
 *  `{}`-stub lesson, applied to the droom before it can recur here). */
export function isDeckComplete(locale: string): boolean {
  const deck = DECKS[locale];
  if (!deck || locale === 'en') return locale === 'en';
  return Object.keys(EN_DECK).every((key) => {
    const value = deck[key];
    return typeof value === 'string' && value.trim() !== '';
  });
}

/** The set the switcher offers. Computed, exported for the completeness test. */
export function offerableLocales(packLocales?: PackLocales): string[] {
  return (AVAILABLE_LOCALES as readonly string[]).filter(
    (locale) => (locale === 'en' || isDeckComplete(locale)) && packAllows(locale, packLocales),
  );
}

const STORAGE_KEY = 'vcip-droom-locale';

interface LocaleContextValue {
  locale: string;
  /** 'rtl' for Arabic — also stamped on <html dir>. */
  dir: 'rtl' | 'ltr';
  /** What this session may offer: `offerableLocales(packLocales)`. */
  offered: readonly string[];
  setLocale: (next: string) => void;
  /** t(key, vars) — active deck, EN fallback, `{slot}` interpolation. */
  t: (key: string, vars?: Record<string, string>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(packLocales?: PackLocales): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && offerableLocales(packLocales).includes(stored)) return stored;
  } catch {
    /* storage unavailable (private mode) — default en */
  }
  return 'en';
}

export function LocaleProvider({
  children,
  packLocales,
}: {
  children: ReactNode;
  /** VC-12: the session pack's customer languages (session read `packLocales`); absent on India sessions. */
  packLocales?: PackLocales;
}): JSX.Element {
  const [locale, setLocaleState] = useState<string>(() => readStoredLocale(packLocales));
  const packKey = Array.isArray(packLocales) ? packLocales.join(',') : '';
  const offered = useMemo(() => offerableLocales(packLocales), [packKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // VC-12: the pack's languages arrive with the session read, after the first render. Arabic this device remembered
  // becomes valid only now; a language the session does not offer (Arabic on an India session) is dropped back to the
  // stored choice or English — never left on screen.
  useEffect(() => {
    setLocaleState((prev) => (offered.includes(prev) && prev !== 'en' ? prev : readStoredLocale(packLocales)));
  }, [packKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const dir = dirOf(locale);

  // The honest <html lang> — index.html pins "en"; the runtime re-stamps it so
  // screen readers and the browser's font/hyphenation engine see the truth.
  // data-locale drives the Devanagari font-face scope in dataroom.css.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.setAttribute('data-locale', locale);
    // VC-12: the direction at the ROOT, so portals, overlays and the browser's own controls mirror too
    document.documentElement.setAttribute('dir', dir);
  }, [locale, dir]);

  const setLocale = useCallback((next: string) => {
    if (!offered.includes(next)) return; // refuse, never half-render
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* non-persistent is fine — the choice still applies for this visit */
    }
  }, [offered]);

  const t = useCallback(
    (key: string, vars?: Record<string, string>): string => {
      const deck: Record<string, string> | undefined = DECKS[locale];
      // EN is the source of record; a missing key falls back rather than
      // rendering a raw key at a customer (offer-only-complete makes this a
      // belt-and-braces path, not an expected one).
      let text = deck?.[key] ?? (EN_DECK as Record<string, string>)[key] ?? key;
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replace(`{${name}}`, value);
        }
      }
      return text;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, dir, offered, setLocale, t }), [locale, dir, offered, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** The screen-side hook. Throws outside the provider — a droom screen without
 *  i18n is the defect this whole tier exists to end, so it fails loudly. */
export function useT(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useT() outside <LocaleProvider> — mount it in App');
  return ctx;
}

/**
 * Render a deck string's `**bold**` markers as <b> — the ONLY markup decks may
 * carry. Translators keep the markers around the equivalent words, so emphasis
 * (the consent verb, "Allow", "recorded") survives translation without any
 * fragment-joining.
 */
export function rich(text: string): ReactNode {
  const parts = text.split('**');
  if (parts.length < 3) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? <b key={`${i}-${part}`}>{part}</b> : part,
  );
}

/**
 * A queue position, phrased per locale. English uses ordinal suffixes
 * ("3rd"); Hindi phrases position as a plain numeral inside the sentence
 * ("स्थान 3") — the audit's "English-only ordinal logic" finding, closed by
 * making the FORMAT a locale decision instead of string math in a component.
 */
export function formatPosition(locale: string, n: number): string {
  if (locale === 'en') {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
  }
  return String(n);
}
